import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../layouts/App/hooks/useAppSelector';
import { logout } from '../../../../store/slices/authSlice';
import './SideNav.css';

export type V2NavSection = 'feed' | 'trips' | 'explore' | 'messages' | 'calendar' | 'settings' | 'ask-ai';

interface NavItem {
  id: V2NavSection;
  label: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'feed',
    label: 'Feed',
    path: '/feed',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'trips',
    label: 'Trips',
    path: '/trips',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    id: 'calendar',
    label: 'Calendar',
    path: '/calendar',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'explore',
    label: 'Explore',
    path: '/explore',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs>
    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop stop-color="#0d4f3c"/>
      <stop offset="1" stop-color="#d9ff31"/>
    </linearGradient>
  </defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>
`)}`;

const latestNotifications = [
  'Sofia liked your Santorini post',
  'Tokyo Lights itinerary was updated',
  'Bali Adventure is trending this week',
  'Your calendar sync completed',
  'New deal found for Lisbon flights',
];

const latestMessages = [
  { name: 'Maya Chen', text: 'Are you still free for the Lisbon walk?', unread: true },
  { name: 'Leo Garcia', text: 'Shared a new Rome food list with you.', unread: true },
  { name: 'Emma Wilson', text: 'Santorini tips are ready.', unread: false },
  { name: 'Nora Smith', text: 'Can we move the museum stop?', unread: false },
  { name: 'Trip Concierge', text: 'Your weekend plan has 3 new ideas.', unread: true },
];

interface SideNavProps {
  activeSection: V2NavSection;
}

const SideNav: React.FC<SideNavProps> = ({ activeSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { userProfile, appSyncUser } = useAppSelector((state) => state.auth);
  const avatar = appSyncUser?.picture_url || appSyncUser?.avatarUrl || userProfile?.avatar || fallbackAvatar;

  const isActive = (item: NavItem) => {
    if (item.id === activeSection) return true;
    if (item.id === 'feed') {
      return location.pathname === '/feed' || location.pathname === '/';
    }
    return location.pathname.startsWith(item.path);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const renderNavItems = (onNavigate: (path: string) => void) => (
    <ul className="v2-side-nav__list">
      {NAV_ITEMS.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={`v2-side-nav__item ${isActive(item) ? 'v2-side-nav__item--active' : ''}`}
            onClick={() => onNavigate(item.path)}
            aria-current={isActive(item) ? 'page' : undefined}
            aria-label={item.label}
            title={item.label}
          >
            {item.icon}
            <span className="v2-side-nav__label">{item.label}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <nav className="v2-side-nav" aria-label="Main navigation">
      <div className="v2-side-nav__leading">
        <button type="button" className="v2-side-nav__brand" onClick={() => handleNavigate('/feed')}>
          <span className="v2-side-nav__mark">E</span>
          <span className="v2-side-nav__brand-name">EnRoute</span>
        </button>

        <div className="v2-side-nav__nav-wrap v2-side-nav__nav-wrap--inline">
          {renderNavItems(handleNavigate)}
        </div>
      </div>

      <div className="v2-side-nav__nav-wrap v2-side-nav__nav-wrap--desktop">
        {renderNavItems(handleNavigate)}
      </div>

      <div className="v2-side-nav__actions">
        <label className="v2-side-nav__search">
          <span className="v2-side-nav__search-icon" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <input type="search" placeholder="Search" aria-label="Search" />
        </label>
        <button
          type="button"
          className={`v2-side-nav__ask ${activeSection === 'ask-ai' || location.pathname === '/ask-ai' ? 'v2-side-nav__ask--active' : ''}`}
          onClick={() => navigate('/ask-ai')}
        >
          Ask AI
        </button>
        <div className="v2-side-nav__action-menu">
          <button type="button" className="v2-side-nav__icon-button" aria-label="Show notifications">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="v2-side-nav__dot" />
          </button>
          <div className="v2-side-nav__dropdown" role="status">
            <h3>Notifications</h3>
            {latestNotifications.map((notification) => (
              <p key={notification}>{notification}</p>
            ))}
          </div>
        </div>
        <div className="v2-side-nav__action-menu">
          <button
            type="button"
            className={`v2-side-nav__icon-button ${activeSection === 'messages' ? 'v2-side-nav__icon-button--active' : ''}`}
            onClick={() => navigate('/messages')}
            aria-label="Open messages"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="v2-side-nav__dot" />
          </button>
          <div className="v2-side-nav__dropdown v2-side-nav__dropdown--messages">
            <h3>Messages</h3>
            {latestMessages.map((message) => (
              <div key={message.name} className="v2-side-nav__message-preview">
                <span className={message.unread ? 'v2-side-nav__message-dot' : ''} />
                <div>
                  <strong>{message.name}</strong>
                  <p>{message.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="v2-side-nav__action-menu">
          <button
            type="button"
            className={`v2-side-nav__avatar ${activeSection === 'settings' ? 'v2-side-nav__avatar--active' : ''}`}
            aria-label="Open user menu"
          >
            <img
              src={avatar}
              alt=""
              onError={(event) => {
                event.currentTarget.src = fallbackAvatar;
              }}
            />
          </button>
          <div className="v2-side-nav__dropdown v2-side-nav__dropdown--user">
            <button type="button" onClick={() => navigate('/profile')}>Profile</button>
            <button type="button" onClick={() => navigate('/settings')}>Settings</button>
            <button
              type="button"
              onClick={() => {
                dispatch(logout());
                navigate('/', { replace: true });
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default SideNav;
